/**
 * PropertyMapView - Interactive map view for property listings
 * Uses Leaflet for map rendering with marker clusters
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Bed, Bath, Square, MapPin, 
  ExternalLink, Heart, ChevronLeft, ChevronRight,
  Maximize2, Minimize2, List
} from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom cluster icon
const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 'small';
  let className = 'bg-blue-500';
  
  if (count >= 10) {
    size = 'large';
    className = 'bg-blue-700';
  } else if (count >= 5) {
    size = 'medium';
    className = 'bg-blue-600';
  }
  
  const sizeMap = { small: 36, medium: 44, large: 52 };
  const pixelSize = sizeMap[size];
  
  return L.divIcon({
    html: `<div class="cluster-marker ${className}" style="
      width: ${pixelSize}px;
      height: ${pixelSize}px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
      color: white;
      font-weight: 700;
      font-size: ${size === 'large' ? '16px' : size === 'medium' ? '14px' : '12px'};
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
      border: 3px solid white;
    ">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(pixelSize, pixelSize, true),
  });
};

// Custom marker icon for properties
const createPropertyIcon = (price, currency, isSelected = false) => {
  const formattedPrice = price >= 1000 
    ? `${currency}${(price / 1000).toFixed(0)}k` 
    : `${currency}${price}`;
  
  return L.divIcon({
    className: 'custom-property-marker',
    html: `
      <div class="property-marker ${isSelected ? 'selected' : ''}" style="
        background: ${isSelected ? '#2563eb' : '#ffffff'};
        color: ${isSelected ? '#ffffff' : '#1f2937'};
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        border: 2px solid ${isSelected ? '#2563eb' : '#e5e7eb'};
        cursor: pointer;
        transition: all 0.2s;
      ">
        ${formattedPrice}
      </div>
    `,
    iconSize: [60, 30],
    iconAnchor: [30, 30],
    popupAnchor: [0, -30],
  });
};

// Map bounds controller
const MapBoundsController = ({ listings }) => {
  const map = useMap();
  
  useEffect(() => {
    if (listings && listings.length > 0) {
      const validListings = listings.filter(l => l.latitude && l.longitude);
      if (validListings.length > 0) {
        const bounds = L.latLngBounds(
          validListings.map(l => [l.latitude, l.longitude])
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
      }
    }
  }, [listings, map]);
  
  return null;
};

// Property Card in sidebar
const PropertyCard = ({ listing, isSelected, onSelect, onView, currency }) => (
  <Card 
    className={`p-3 cursor-pointer transition-all hover:shadow-md ${
      isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : ''
    }`}
    onClick={onSelect}
    data-testid={`property-card-${listing.id}`}
  >
    {/* Image */}
    <div className="relative aspect-[16/10] rounded-lg overflow-hidden mb-2">
      <img 
        src={listing.images?.[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400'}
        alt={listing.title}
        className="w-full h-full object-cover"
      />
      {listing.featured && (
        <Badge className="absolute top-2 left-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs">
          Featured
        </Badge>
      )}
      <button 
        className="absolute top-2 right-2 p-1.5 bg-white/90 rounded-full hover:bg-white"
        onClick={(e) => { e.stopPropagation(); }}
      >
        <Heart className="w-4 h-4 text-gray-600" />
      </button>
    </div>
    
    {/* Details */}
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="font-bold text-lg text-blue-600">
          {currency}{listing.price?.toLocaleString()}
          <span className="text-xs font-normal text-gray-500">/mo</span>
        </span>
        {listing.property_type && (
          <Badge variant="secondary" className="text-xs">
            {listing.property_type}
          </Badge>
        )}
      </div>
      
      <h4 className="font-medium text-sm line-clamp-1">{listing.title}</h4>
      
      <div className="flex items-center text-xs text-gray-500 gap-1">
        <MapPin className="w-3 h-3" />
        <span className="line-clamp-1">{listing.location}</span>
      </div>
      
      {/* Features */}
      <div className="flex items-center gap-3 text-xs text-gray-600 pt-1">
        {listing.bedrooms !== undefined && (
          <span className="flex items-center gap-1">
            <Bed className="w-3 h-3" />
            {listing.bedrooms === 0 ? 'Studio' : `${listing.bedrooms} bed`}
          </span>
        )}
        {listing.bathrooms && (
          <span className="flex items-center gap-1">
            <Bath className="w-3 h-3" />
            {listing.bathrooms} bath
          </span>
        )}
        {listing.sqft && (
          <span className="flex items-center gap-1">
            <Square className="w-3 h-3" />
            {listing.sqft} sqft
          </span>
        )}
      </div>
    </div>
    
    {/* View button */}
    <Button 
      size="sm" 
      className="w-full mt-2 bg-blue-600 hover:bg-blue-700 h-8 text-xs"
      onClick={(e) => { e.stopPropagation(); onView(listing); }}
    >
      View Property
      <ExternalLink className="w-3 h-3 ml-1" />
    </Button>
  </Card>
);

// Main Map View Component
export default function PropertyMapView({ 
  listings = [], 
  country = 'LK',
  onListView,
  className = '' 
}) {
  const [selectedListing, setSelectedListing] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const mapRef = useRef(null);
  
  const currency = country === 'UK' ? '£' : 'Rs.';
  
  // Default map center based on country
  const defaultCenter = country === 'UK' 
    ? [51.5074, -0.1278] // London
    : [6.9271, 79.8612]; // Colombo
  
  // Filter listings with valid coordinates
  const mappableListings = useMemo(() => {
    return listings.filter(l => {
      // Check for actual coordinates or generate based on location
      return l.latitude && l.longitude;
    });
  }, [listings]);
  
  // Generate approximate coordinates for listings without them (demo purposes)
  const allListingsWithCoords = useMemo(() => {
    return listings.map((listing, index) => {
      if (listing.latitude && listing.longitude) {
        return listing;
      }
      // Generate random offset from center for demo
      const latOffset = (Math.random() - 0.5) * 0.1;
      const lngOffset = (Math.random() - 0.5) * 0.1;
      return {
        ...listing,
        latitude: defaultCenter[0] + latOffset,
        longitude: defaultCenter[1] + lngOffset,
      };
    });
  }, [listings, defaultCenter]);
  
  const handleMarkerClick = (listing) => {
    setSelectedListing(listing);
  };
  
  const handleViewProperty = (listing) => {
    window.open(`/listing/${listing.id}`, '_blank');
  };
  
  return (
    <div 
      className={`relative bg-gray-100 ${isFullscreen ? 'fixed inset-0 z-50' : 'h-[600px]'} ${className}`}
      data-testid="property-map-view"
    >
      {/* Top Controls */}
      <div className="absolute top-4 left-4 right-4 z-[1000] flex items-center justify-between">
        <Button 
          variant="secondary"
          size="sm"
          onClick={onListView}
          className="shadow-lg bg-white"
          data-testid="switch-to-list-btn"
        >
          <List className="w-4 h-4 mr-2" />
          List View
        </Button>
        
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="icon"
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="shadow-lg bg-white h-8 w-8"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
        </div>
      </div>
      
      {/* Sidebar Toggle (mobile) */}
      <button
        className={`absolute z-[1000] top-1/2 -translate-y-1/2 ${
          sidebarCollapsed ? 'right-0' : 'right-80'
        } bg-white shadow-lg p-2 rounded-l-lg transition-all md:hidden`}
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
      >
        {sidebarCollapsed ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      
      {/* Sidebar with listings */}
      <div 
        className={`absolute top-0 right-0 h-full bg-white shadow-xl z-[999] transition-all duration-300 ${
          sidebarCollapsed ? 'w-0 overflow-hidden' : 'w-80'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800">
              {listings.length} Properties
            </h3>
            <p className="text-xs text-gray-500">Click markers to view details</p>
          </div>
          
          {/* Listings scroll area */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {allListingsWithCoords.map(listing => (
              <PropertyCard
                key={listing.id}
                listing={listing}
                isSelected={selectedListing?.id === listing.id}
                onSelect={() => handleMarkerClick(listing)}
                onView={handleViewProperty}
                currency={currency}
              />
            ))}
            
            {listings.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <MapPin className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p>No properties in this area</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Map Container */}
      <MapContainer
        ref={mapRef}
        center={defaultCenter}
        zoom={12}
        className={`h-full w-full ${sidebarCollapsed ? '' : 'md:pr-80'}`}
        zoomControl={false}
        style={{ zIndex: 1 }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapBoundsController listings={allListingsWithCoords} />
        
        {/* Property Markers with Clustering */}
        <MarkerClusterGroup
          chunkedLoading
          iconCreateFunction={createClusterCustomIcon}
          maxClusterRadius={60}
          spiderfyOnMaxZoom={true}
          showCoverageOnHover={false}
          zoomToBoundsOnClick={true}
          disableClusteringAtZoom={16}
        >
          {allListingsWithCoords.map(listing => (
            <Marker
              key={listing.id}
              position={[listing.latitude, listing.longitude]}
              icon={createPropertyIcon(listing.price, currency, selectedListing?.id === listing.id)}
              eventHandlers={{
                click: () => handleMarkerClick(listing),
              }}
            >
              <Popup>
                <div className="w-52">
                  <img 
                    src={listing.images?.[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=300'}
                    alt={listing.title}
                    className="w-full h-28 object-cover rounded mb-2"
                  />
                  <h4 className="font-semibold text-sm mb-1">{listing.title}</h4>
                  <p className="text-blue-600 font-bold">
                    {currency}{listing.price?.toLocaleString()}/mo
                  </p>
                  <div className="flex gap-2 text-xs text-gray-600 mt-1">
                    {listing.bedrooms !== undefined && (
                      <span>{listing.bedrooms === 0 ? 'Studio' : `${listing.bedrooms} bed`}</span>
                    )}
                    {listing.property_type && <span>• {listing.property_type}</span>}
                  </div>
                  <Button 
                    size="sm" 
                    className="w-full mt-2 h-7 text-xs"
                    onClick={() => handleViewProperty(listing)}
                  >
                    View Details
                  </Button>
                </div>
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      </MapContainer>
      
      {/* Results count overlay */}
      <div className="absolute bottom-4 left-4 z-[1000]">
        <Badge className="bg-white text-gray-800 shadow-lg px-3 py-1.5">
          {listings.length} properties found
        </Badge>
      </div>
    </div>
  );
}
